// 작성 탭 캔버스 분기 위젯 슬롯 탭 오버레이 — 본문(양식 탭) BranchSlotTabsOverlay의 작성폼(위젯) 대응물.
//
// 편집 캔버스는 branch 위젯의 "활성 슬롯"(activeBranchSlots[id]) children만 렌더하므로(조건 미평가),
// 이 탭이 유일한 슬롯 전환 수단이다(속성 패널·트리 슬롯 서브행과 공유 state = activeBranchSlots).
//
// v1 방식(중첩=DOM 재귀): 트리의 모든 branch 위젯을 훑되, 그 위젯 DOM(`[data-composer-widget-id]`)이
// 실제로 캔버스에 존재하는(=활성 경로상) 것만 탭 바를 띄운다. 비활성 슬롯 속 중첩 branch는 DOM 부재라
// 자동 제외되고, 부모 슬롯 전환 시 그 지점부터 자연 재구성된다(본문의 명시적 체인 계산 대신 DOM 존재로
// 재귀를 표현). 반복(repeat) 영역은 행마다 같은 위젯 id를 렌더하므로 인스턴스마다 바 1개다.
//
// 표시·상호작용은 본문과 **한 벌을 공유**한다(BranchSlotTabBar): 자기 위젯 rect 바로 위에 전체 슬롯
// 탭 바를 상시 둔다(2026-08-31 사용자 확정 — 원래 방식, 두 캔버스 같은 UI/UX가 방침이다). 같은 날
// 시도한 두 대안은 되돌렸다 — 캔버스 왼쪽 여백 열은 시선 왕복 때문에, 활성 슬롯만 담은 컴팩트 칩 +
// hover 확장은 선택지가 마우스를 올려야만 보였기 때문에.
//   - 아랫변을 위젯 윗변에 맞추므로(nodeTabBarBox) 한 줄 바는 제자리, 줄이 접히면 위로만 자란다.
//   - 겹침은 본문과 같은 `resolveTabBarOverlaps`(겹칠 때만 아래로 밀기)로 푼다.
//
// ⚠️ **이 오버레이는 작성 캔버스가 아니라 CanvasViewport의 A4 박스 직속(overlays render-prop)에 산다.**
// 작성 캔버스(`.composer-write-canvas`)는 실제 작성 화면 셸을 흉내내는 ComposeChromePreviewFrame의
// `<Card className="overflow-hidden">` **안**이라, 거기 두면 박스 밖으로 나가는 좌표가 에러 없이 잘린다
// (그 클립은 프레임 라운드 코너를 위한 것이라 걷어낼 수 없다 — 실작성 화면 패리티). 바가 노드 위에
// 있어도 이 층은 그대로 필요하다: 폼 **맨 위** 분기 위젯의 바는 `위젯 top - 높이 - lift`가 음수라
// 박스 위쪽 바깥에 그려지고, 캔버스 안에 두면 딱 그 바만 잘린다(가장 눈에 띄는 자리인데).
//   → 그래서 좌표계도 본문과 같아진다: 박스는 **미스케일** 공간이고 rect 차분은 시각 px라 그 값이 곧
//     화면 위치다(`rectRelativeTo`). 줌 래퍼 안에 있을 때 필요했던 역보정(rectRelativeToUnscaled)은
//     여기선 오히려 이중 보정이 되므로 쓰지 않는다.
//   → 위젯 DOM 조회만은 **작성 캔버스로 스코프**한다. 비교 패널(effectiveCompare)이 같은 위젯 id를
//     한 벌 더 렌더하므로 박스 전체에서 찾으면 비교본에도 탭이 붙는다.
//
// 편집 캔버스 전용 — 인쇄/미리보기/실폼(web)에는 포함되지 않는다. 레이아웃 중립(absolute, pointer-events는 탭에만).

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ComposerWidget, BranchWidgetSlot } from '@safetyhub/shared';
import { cssEscape, rectRelativeTo } from './canvasRect';
import {
  estimatedBarTop,
  nodeTabBarBox,
  resolveTabBarOverlaps,
  type TabBarBox,
} from './branchTabLayout';
import { BranchSlotTabBar, tabBarSelector } from './BranchSlotTabBar';
// 라벨·활성 슬롯 규약은 트리 순수 함수로 분리해 둔다(전개 규칙이 렌더와 갈라지지 않게).
import { activeBranchWidgets, activeSlotIndex, slotTabLabel } from './writeBranchTabs';

/** 렌더할 탭 바 1개 — 반복 전개로 같은 branch가 여러 인스턴스면 각각 1개다. */
interface WriteTabBar {
  /** 최종 top·측정 앵커·React key 공용 식별자(`branchId#인스턴스순번`). */
  barKey: string;
  branchId: string;
  slots: BranchWidgetSlot[];
  activeIndex: number;
  /** 기준이 되는 위젯 rect 좌상단 — **매번 다시** 계산한 값(밀림 누적 방지). */
  nodeTop: number;
  nodeLeft: number;
}

/**
 * 활성 경로에 실제로 렌더된 branch 위젯을 찾아 탭 바 목록을 만든다. DOM 조회·rect 측정을 하므로
 * **layout effect에서만** 부른다(렌더 본문 호출 금지 — 커밋 전 레이아웃을 강제로 읽게 된다).
 *
 * 후보는 `activeBranchWidgets`(순수 활성 경로 전개)로 좁히고, 실제 렌더 여부는 DOM 존재로 최종
 * 판정한다. 모달을 열지 않아 DOM이 없는 branch는 여기서 걸러지므로, 순수 전개가 후보를 조금 넓게
 * 잡아도 무해하다.
 *
 * 순서는 트리 순서(부모 → 자식)다. `resolveTabBarOverlaps`가 top 동률을 **안정 정렬**로 깨므로,
 * 부모와 top이 같은 중첩 branch는 부모가 제자리를 지키고 자식이 밀린다.
 */
function renderedWriteTabBars(
  box: HTMLElement,
  canvas: HTMLElement,
  tree: ComposerWidget[],
  activeBranchSlots: Record<string, number>,
): WriteTabBar[] {
  const bars: WriteTabBar[] = [];
  for (const branch of activeBranchWidgets(tree, activeBranchSlots)) {
    // 활성 경로상 branch만 DOM 존재. 모달 미개방 등으로 안 그려진 branch는 el 부재로 제외.
    const els = canvas.querySelectorAll<HTMLElement>(
      `[data-composer-widget-id="${cssEscape(branch.id)}"]`,
    );
    if (els.length === 0) continue;
    // 활성 슬롯은 템플릿 레벨 state(activeBranchSlots)라 전 인스턴스가 함께 전환된다.
    const activeIndex = activeSlotIndex(branch, activeBranchSlots);
    els.forEach((el, instIdx) => {
      const rect = rectRelativeTo(box, el);
      bars.push({
        barKey: `${branch.id}#${instIdx}`,
        branchId: branch.id,
        slots: branch.branches,
        activeIndex,
        nodeTop: rect.top,
        nodeLeft: rect.left,
      });
    });
  }
  return bars;
}

/**
 * 실측된 탭 바들의 최종 top(기준 + 겹침 밀기)을 계산하고, **그 자리에서 DOM에 반영**한다
 * (본문 선례 동형).
 *
 * ⚠️ 기준은 **위젯 rect에서 매번 다시** 계산한다 — 바 자신의 현재 위치를 기준으로 삼으면 이미 적용된
 * 밀기가 다음 회차의 기준이 돼 바가 커밋마다 계속 내려간다(발산). 그래서 기준(nodeTop)은 호출부가
 * 같은 tick에 새로 잰 `bars`로 넘기고, 여기서 바에게 묻는 건 폭·높이뿐이다 — 그 둘은 위치를 옮겨도
 * 변하지 않으므로(가로 상한이 상수) 계산이 수렴한다.
 *
 * style.top을 직접 맞춰 두면 밀리기 전 위치가 paint에 비치지 않는다. 반환한 **최종 top**은 state로도
 * 보관해 다음 렌더의 JSX가 DOM과 어긋나지 않게 한다(아랫변 고정이라 기준 top이 실측 높이에 달려 있어,
 * 오프셋만 넘기면 JSX가 그 기준을 혼자 재현하지 못한다).
 */
function measureAndApplyTops(box: HTMLElement, bars: WriteTabBar[]): Record<string, number> {
  const boxes: TabBarBox[] = [];
  const elements = new Map<string, HTMLElement>();
  for (const bar of bars) {
    const el = box.querySelector<HTMLElement>(tabBarSelector('write', bar.barKey));
    if (!el) continue; // 첫 커밋/렌더 직전 — 다음 측정 tick에서 잡힌다.
    const barRect = rectRelativeTo(box, el);
    elements.set(bar.barKey, el);
    boxes.push(
      nodeTabBarBox(bar.barKey, bar.nodeTop, bar.nodeLeft, barRect.width, barRect.height),
    );
  }

  const offsets = resolveTabBarOverlaps(boxes);
  const tops: Record<string, number> = {};
  for (const b of boxes) {
    const top = b.top + (offsets[b.id] ?? 0);
    tops[b.id] = top;
    // 안 밀린 바에도 다시 써 둔다(밀렸다가 겹침이 풀린 바가 옛 자리를 붙든 채 남지 않게).
    const el = elements.get(b.id);
    if (el) el.style.top = `${top}px`;
  }
  return tops;
}

/**
 * 작성 캔버스 분기 슬롯 탭 오버레이 — **CanvasViewport의 A4 박스 직속**(overlays render-prop)에 둔다.
 * 각 활성 경로 branch 위젯 바로 위에 전체 슬롯 탭 바 1개(반복 영역은 인스턴스마다 1개).
 * 탭 클릭 시 그 branch의 활성 슬롯을 전환하고 branch 위젯을 선택한다.
 */
export function WriteCanvasBranchTabsOverlay({
  box,
  canvas,
  tree,
  activeBranchSlots,
  onSelectSlot,
  onSelectWidget,
  /** rect 재측정 트리거(트리/활성 슬롯/뷰 변화 등). 값이 바뀌면 재측정한다. */
  remeasureKey,
}: {
  /** 좌표 기준 + 오버레이 컨테이너인 캔버스 박스(미스케일). CanvasViewport가 넘긴다. */
  box: HTMLElement | null;
  /** 위젯 DOM 조회 스코프(작성 캔버스) — 비교 패널의 같은 id를 배제하려면 반드시 이 범위로. */
  canvas: HTMLElement | null;
  tree: ComposerWidget[];
  activeBranchSlots: Record<string, number>;
  onSelectSlot: (branchId: string, index: number) => void;
  onSelectWidget: (branchId: string) => void;
  remeasureKey?: unknown;
}) {
  // 바 목록·최종 top 모두 **DOM 실측 결과**라 렌더 본문이 아니라 layout effect에서만 만든다
  // (BranchLinkOverlay/BindingBadgeOverlay와 같은 관례 — 렌더 중 getBoundingClientRect 금지).
  const [bars, setBars] = useState<WriteTabBar[]>([]);
  const [tops, setTops] = useState<Record<string, number>>({});
  const sigRef = useRef<string>('');
  // rect는 DOM 측정이라 렌더 후 확정 — layout tick으로 재측정(마운트/캔버스 리사이즈/뷰 변화 시).
  const [, setTick] = useState(0);
  useEffect(() => {
    setTick((n) => n + 1);
    if (!canvas) return;
    // 캔버스 리사이즈(패널 스플리터·줌)·창 리사이즈 시 재측정.
    const ro = new ResizeObserver(() => setTick((n) => n + 1));
    ro.observe(canvas);
    const onResize = (): void => setTick((n) => n + 1);
    window.addEventListener('resize', onResize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [canvas, remeasureKey]);

  // ⚠️ 측정은 커밋 후(useLayoutEffect), deps 없이 매 커밋마다 수행하고 시그니처가 바뀔 때만 setState
  // (본문 BranchSlotTabsOverlay와 동일 관례). layout effect라 위치 보정이 paint 전에 반영된다.
  // 슬롯 라벨·개수가 바뀌어 줄 접힘이 달라지는 경우도 이 경로로 재측정된다.
  //
  // 바 목록과 top을 **한 tick에 같이** 잰다. 바가 아직 안 그려진 첫 회차엔 tops가 비지만, setBars로
  // 바가 그려진 뒤 곧바로 이 effect가 다시 돌아 실측 top이 채워진다 — layout effect의 setState는
  // paint 전에 동기 flush되므로 추정 위치가 화면에 비치지 않는다.
  useLayoutEffect(() => {
    const nextBars =
      box && canvas ? renderedWriteTabBars(box, canvas, tree, activeBranchSlots) : [];
    const nextTops = box ? measureAndApplyTops(box, nextBars) : {};
    const sig = JSON.stringify([nextBars, nextTops]);
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setBars(nextBars);
      setTops(nextTops);
    }
  }); // deps 없음 — 매 커밋(재측정 tick 포함) 후 최신 DOM 측정

  if (!box || !canvas) return null;

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden={false}>
      {bars.map((bar) => (
        <BranchSlotTabBar
          key={`wt-branch-tabs-${bar.barKey}`}
          surface="write"
          measureKey={bar.barKey}
          branchId={bar.branchId}
          slots={bar.slots.map((slot, i) => ({ id: slot.id, label: slotTabLabel(slot, i) }))}
          activeIndex={bar.activeIndex}
          // 실측된 최종 top을 그대로 쓴다. 아직 측정 전인 첫 커밋만 한 줄 바 기준의 추정값으로
          // 시작하고, 같은 커밋의 layout effect가 실측값으로 덮는다(paint 전).
          top={tops[bar.barKey] ?? estimatedBarTop(bar.nodeTop)}
          left={bar.nodeLeft}
          onSelectSlot={(index) => {
            onSelectSlot(bar.branchId, index);
            onSelectWidget(bar.branchId);
          }}
        />
      ))}
    </div>
  );
}
